import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Table,
  IconButton,
  TextField,
  Modal,
  Button,
} from "@mui/material";
import { headingStyle, tableRowLab } from "../../style/LapPageStyle";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import "react-toastify/dist/ReactToastify.css";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { drTableGreen } from "../../style/PatinetFormStyle";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import {
  deleteExternalLabDocument,
  deleteExternalLabReport,
  deleteExternalLabreportMultiple,
} from "../../connections/deleteApi";
import { showToast } from "../../utils/toastUtil";
import { postMedicalReport } from "../../connections/postApi";
import { textfieldText } from "../../style/AppointmentStyle";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";

const ViewExternalReports = ({
  patientData = {},
  onClose,
  date,
  patientReports,
  patientFileReports,
}) => {
  // =========================
  // Local UI state (ONLY UI update)
  // =========================
  const [reportsUI, setReportsUI] = useState([]);
  const [docsUI, setDocsUI] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteModalFile, setDeleteModalFile] = useState(false);
  const [deleteFileTarget, setDeleteFileTarget] = useState(null);

  // deleteTarget shape:
  // { type: "normal" | "composite", id: number, label: string }

  const openDeleteModal = ({ type, ids, label }) => {
    setDeleteTarget({ type, ids, label });
    setDeleteModal(true);
  };

  const openDeleteFileModal = ({ id, label }) => {
    setDeleteFileTarget({ id, label });
    setDeleteModalFile(true);
  };

  const handleDelete = async () => {
    const ids = deleteTarget?.ids || [];
    if (!ids.length) return;

    try {
      const res = await deleteExternalLabreportMultiple(ids);

      if (res?.status?.status === "SUCCESS") {
        // ✅ UI remove
        setReportsUI((prev) => {
          // NORMAL report removed by its id (first id)
          if (deleteTarget.type === "normal") {
            const normalId = ids[0];
            return prev.filter(
              (r) => !(r?.isComposite === false && r?.id === normalId),
            );
          }

          // SUBTEST delete: remove only that subtest from composites
          if (deleteTarget.type === "subtest") {
            const subId = ids[0];
            return (
              prev
                .map((r) => {
                  if (!r?.isComposite) return r;
                  return {
                    ...r,
                    composites: (r?.composites || []).filter(
                      (c) => c?.id !== subId,
                    ),
                  };
                })
                // optional: if a composite parent becomes empty, remove it
                .filter(
                  (r) =>
                    !(r?.isComposite && (r?.composites || []).length === 0),
                )
            );
          }

          // COMPOSITE parent delete: remove the parent entry entirely
          // (because you deleted all its subtests)
          if (deleteTarget.type === "composite") {
            // easiest: remove any composite parent whose composites were deleted
            // We’ll remove the parent if ANY of its subtest ids match deleted ids
            const idSet = new Set(ids);
            return prev.filter((r) => {
              if (!r?.isComposite) return true;
              const subIds = (r?.composites || []).map((c) => c?.id);
              return !subIds.some((sid) => idSet.has(sid));
            });
          }

          return prev;
        });

        showToast(res?.status?.message || "Deleted", "SUCCESS");
      } else {
        showToast(res?.status?.message || "Delete failed", "ERROR");
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast("Something went wrong", "ERROR");
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const originalValueRef = useRef({}); // key -> original value

  const makeKey = (type, id) => `${type}_${id}`;

  // =========================
  // Normalize reports (composite + normal)
  // =========================
  useEffect(() => {
    const raw = Array.isArray(patientReports) ? patientReports : [];

    const normalized = raw.map((item) => {
      const isComposite =
        Array.isArray(item?.composites) && item?.composites?.length > 0;

      if (isComposite) {
        return {
          isComposite: true,
          medical_test_id: item?.medical_test_id,
          parent_test_name: item?.parent_test_name,
          parent_test_code: item?.parent_test_code,
          amount: item?.amount,

          parent_report_id:
            item?.report_id ?? item?.id ?? item?.medical_test_report_id,

          // ✅ IMPORTANT: enrich each sub-test so edit payload can work
          composites: (item?.composites || []).map((c) => ({
            ...c,

            // try to attach required fields (fallbacks)
            patient_id: c?.patient_id ?? item?.patient_id ?? patientData?.id,
            invoice_item_id: c?.invoice_item_id ?? item?.invoice_item_id,
            composite_parent_test_id:
              c?.composite_parent_test_id ?? item?.medical_test_id,
            report_id: c?.report_id ?? item?.report_id ?? c?.id, // API expects report_id
            medical_test_report_id: c?.medical_test_report_id ?? c?.id, // API expects id field (optional)
          })),
        };
      }

      // ✅ normal report
      return {
        isComposite: false,
        ...item,
        test_name: item?.test_name || item?.name,
      };
    });

    setReportsUI(normalized);
  }, [patientReports, patientData?.id]);

  useEffect(() => {
    setDocsUI(Array.isArray(patientFileReports) ? patientFileReports : []);
  }, [patientFileReports]);

  // =========================
  // UI update helpers
  // =========================
  const updateReportValueUI = (reportId, newValue) => {
    setReportsUI((prev) =>
      prev.map((r) => (r?.id === reportId ? { ...r, value: newValue } : r)),
    );
  };

  const updateCompositeValueUI = (
    parentMedicalTestId,
    compositeId,
    newValue,
  ) => {
    setReportsUI((prev) =>
      prev.map((r) => {
        if (!r?.isComposite) return r;
        if (r?.medical_test_id !== parentMedicalTestId) return r;

        return {
          ...r,
          composites: (r?.composites || []).map((c) =>
            c?.id === compositeId ? { ...c, value: newValue } : c,
          ),
        };
      }),
    );
  };

  // =========================
  // EDIT: Normal test
  // =========================
  const handleEditMedicalReportValue = async (item, newValue) => {
    if (newValue === undefined || newValue === null) return;

    const key = makeKey("normal", item?.id);
    const oldValue = originalValueRef.current[key];

    // ✅ if never focused, fallback oldValue = item.value (rare case)
    const prevValue = oldValue !== undefined ? oldValue : (item?.value ?? "");

    // ✅ compare with ORIGINAL value
    if (String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }

    const payload = {
      medical_test_report: {
        patient_id: item?.patient_id,
        medical_test_id: item?.medical_test_id,
        invoice_item_id: item?.invoice_item_id,
        value: newValue,
      },
      id: item?.id,
    };

    try {
      const result = await postMedicalReport(
        payload,
        item?.report_id ?? item?.id,
      );
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch (e) {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key]; // ✅ cleanup
    }
  };

  // =========================
  // EDIT: Composite sub-test (IMPORTANT)
  // =========================

  const handleEditMedicalReportValueComp = async (item, newValue) => {
    if (newValue === undefined || newValue === null) return;

    const key = makeKey("comp", item?.id);
    const oldValue = originalValueRef.current[key];
    const prevValue = oldValue !== undefined ? oldValue : (item?.value ?? "");

    if (String(prevValue) === String(newValue)) {
      delete originalValueRef.current[key];
      return;
    }

    const payload = {
      medical_test_report: {
        patient_id: item?.patient_id,
        medical_test_id: item?.medical_test_id,
        invoice_item_id: item?.invoice_item_id,
        value: newValue,
        composite_parent_test_id: item?.composite_parent_test_id,
      },
      ...(item?.medical_test_report_id && { id: item?.medical_test_report_id }),
    };

    try {
      const result = await postMedicalReport(payload, item?.report_id);
      showToast(
        result?.status?.message || result?.status?.status,
        result?.status?.status,
      );
    } catch (e) {
      showToast("Something went wrong", "ERROR");
    } finally {
      delete originalValueRef.current[key];
    }
  };

  // =========================
  // DELETE: Report row (normal + composite if needed)
  // =========================
  const deleteReportUI = async (reportId) => {
    try {
      const res = await deleteExternalLabReport(reportId);
      if (res?.status?.status === "SUCCESS") {
        // remove normal report row only (composite sub delete can be handled separately if API supports)
        setReportsUI((prev) => prev.filter((r) => r?.id !== reportId));
      }
    } catch (error) {
      console.error("Error deleting report:", error);
    }
  };

  // =========================
  // DELETE: Attachment
  // =========================
  const deleteAttachmentUI = async (docId) => {
    try {
      const res = await deleteExternalLabDocument(docId);
      if (res?.status?.status === "SUCCESS") {
        setDocsUI((prev) => prev.filter((doc) => doc?.id !== docId));
        showToast(res?.status?.message || "Deleted", "SUCCESS");
        setDeleteModalFile(false);
        setDeleteFileTarget(null);
      } else {
        showToast(res?.status?.message || "Delete failed", "ERROR");
      }
    } catch (error) {
      showToast("Error deleting document", "ERROR");
      console.error("Error deleting document:", error);
    }
  };

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  // viewerFile shape: { url, filename, content_type }

  const openViewer = (file) => {
    if (!file?.url) return;
    setViewerFile(file);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerFile(null);
  };

  return (
    <Box
      sx={{
        width: "80vw",
        p: 1,
        height: "90vh",
        overflowY: "auto",
        "&::-webkit-scrollbar": { display: "none" },
        "-ms-overflow-style": "none",
        scrollbarWidth: "none",
      }}
    >
      {/* ===== Header ===== */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        mt={-0.5}
      >
        <Box />
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ContentPasteIcon sx={{ width: "28px", color: "neutral.teal" }} />
          <Typography
            variant="heading1"
            fontWeight={600}
            gutterBottom
            sx={{ ...headingStyle }}
          >
            External Report viewer
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: "red" }} size="small" />
        </IconButton>
      </Box>

      {/* ===== Patient Info ===== */}
      <Grid
        container
        sx={{ justifyContent: "space-between", width: "100%", mt: "30px" }}
      >
        <Typography variant="subTitle116px">
          Name: {patientData?.full_name || "-"}
        </Typography>
        <Typography variant="subTitle116px">
          Patient ID: {patientData?.uid || "-"}
        </Typography>
        <Typography variant="subTitle116px">
          Mobile: {patientData?.mobile || "-"}
        </Typography>
        <Typography variant="subTitle116px">
          Age: {patientData?.age || "-"}
        </Typography>
        <Typography variant="subTitle116px">
          Gender: {patientData?.gender || "-"}
        </Typography>
      </Grid>

      {/* ================= FILE ATTACHMENTS ================= */}
      {docsUI?.length > 0 && (
        <Box>
          <Box
            sx={{
              flex: "1 1 100%",
              mt: 3,
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            {docsUI.map((doc, idx) => {
              const file = doc?.attachment;
              if (!file) return null;

              const isPDF = file?.content_type === "application/pdf";
              const isImage = file?.content_type?.startsWith("image/");

              return (
                <Box
                  key={doc?.id ?? idx}
                  sx={{
                    width: 300,
                    height: 140,
                    borderRadius: "14px",
                    backgroundColor: "neutral.blueShadeBg",
                    position: "relative",
                    border: "1px solid #E1E1E1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      display: "flex",
                      gap: 0.5,
                    }}
                  >
                    <IconButton size="small" onClick={() => openViewer(file)}>
                      <VisibilityOutlinedIcon
                        sx={{ fontSize: 18, color: "neutral.teal" }}
                      />
                    </IconButton>

                    <IconButton
                      size="small"
                      // onClick={() => deleteAttachmentUI(doc?.id)}
                      onClick={() =>
                        openDeleteFileModal({
                          id: doc?.id,
                          label: file?.filename || "this document",
                        })
                      }
                    >
                      <DeleteForeverOutlinedIcon
                        sx={{ fontSize: 18, color: "red" }}
                      />
                    </IconButton>
                  </Box>

                  <Box
                    sx={{
                      textAlign: "center",
                      flexDirection: "column",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isPDF ? (
                      <PictureAsPdfOutlinedIcon
                        sx={{ fontSize: 30, color: "neutral.teal" }}
                      />
                    ) : isImage ? (
                      <ImageOutlinedIcon
                        sx={{ fontSize: 30, color: "neutral.teal" }}
                      />
                    ) : (
                      <DescriptionOutlinedIcon
                        sx={{ fontSize: 30, color: "neutral.teal" }}
                      />
                    )}

                    <Typography
                      variant="subTitle116px"
                      sx={{
                        mt: 1,
                        maxWidth: "220px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontFamily: "Lato",
                        color: "neutral.black",
                      }}
                    >
                      {file?.filename}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ================= REPORT TABLE ================= */}
      {reportsUI && reportsUI?.length > 0 && (
        <Grid container sm={12} md={12} marginBottom={"auto"} lg={12} mt={2}>
          <Grid item xs={12}>
            <TableContainer
              sx={{
                mt: 2,
                width: "100%",
                border: "1px solid #E1E1E1",
                borderRadius: "15px",
                overflowY: "auto",
                "&::-webkit-scrollbar": { display: "none" },
                "-ms-overflow-style": "none",
                scrollbarWidth: "none",
              }}
            >
              <Table
                sx={{
                  width: "100%",
                  overflowY: "scroll",
                  "&::-webkit-scrollbar": { display: "none" },
                  "-ms-overflow-style": "none",
                  scrollbarWidth: "none",
                }}
              >
                <TableHead stickyHeader>
                  <TableRow
                    sx={{
                      ...drTableGreen,
                      backgroundColor: "neutral.backGround",
                    }}
                  >
                    <TableCell
                      sx={{ borderTopLeftRadius: "14px", textAlign: "center" }}
                    >
                      <Typography variant="body1_text_bold">S.No</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1_text_bold">Name</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1_text_bold">
                        Generated Date
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1_text_bold">
                        Critical Range
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Typography variant="body1_text_bold">
                        Min-Max Range
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{ borderTopRightRadius: "14px", textAlign: "center" }}
                    >
                      <Typography variant="body1_text_bold">
                        Value / Action{" "}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {reportsUI?.map((report, index) => {
                    // ✅ COMPOSITE PARENT
                    if (report?.isComposite) {
                      return (
                        <React.Fragment
                          key={`comp_${report?.medical_test_id ?? index}`}
                        >
                          {/* Parent Row */}
                          <TableRow
                            sx={{
                              ...tableRowLab,
                              backgroundColor: "#F9FBFF",
                              borderBottom: "1px solid #E1E1E1",
                            }}
                          >
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography
                                sx={{ fontSize: "15px", fontWeight: 600 }}
                              >
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Typography
                                sx={{ fontSize: "15px", fontWeight: 700 }}
                              >
                                {report?.parent_test_name}
                              </Typography>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                            <TableCell />

                            <TableCell sx={{ textAlign: "center" }}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openDeleteModal({
                                    type: "composite",
                                    ids: (report?.composites || [])
                                      .map((c) => c?.id)
                                      .filter(Boolean),
                                    label: report?.parent_test_name,
                                  })
                                }
                              >
                                <DeleteForeverOutlinedIcon
                                  sx={{ fontSize: 18, color: "red" }}
                                />
                              </IconButton>
                            </TableCell>
                          </TableRow>

                          {/* Subtable Row */}
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 1 }}>
                              <Box
                                sx={{
                                  border: "1px solid #E5E7EB",
                                  borderRadius: "12px",
                                  overflow: "hidden",
                                }}
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        S.No
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        Name
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        Generated Date
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        Critical
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        Min-Max
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          fontWeight: 700,
                                          textAlign: "center",
                                        }}
                                      >
                                        Value / Action
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>

                                  <TableBody>
                                    {(report?.composites || []).map(
                                      (sub, subIndex) => (
                                        <TableRow key={sub?.id ?? subIndex}>
                                          <TableCell
                                            sx={{ textAlign: "center" }}
                                          >
                                            {subIndex + 1}
                                          </TableCell>
                                          <TableCell
                                            sx={{ textAlign: "center" }}
                                          >
                                            {sub?.name}
                                          </TableCell>
                                          <TableCell
                                            sx={{ textAlign: "center" }}
                                          >
                                            {sub?.generated_at}
                                          </TableCell>
                                          <TableCell
                                            sx={{ textAlign: "center" }}
                                          >
                                            {sub?.critical ?? "-"}
                                          </TableCell>
                                          <TableCell
                                            sx={{ textAlign: "center" }}
                                          >
                                            {sub?.reference_range
                                              ?.male_min_max ?? "-"}
                                          </TableCell>

                                          <TableCell
                                            sx={{ textAlign: "center" }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <TextField
                                                value={sub?.value ?? ""}
                                                onFocus={() => {
                                                  const key = makeKey(
                                                    "comp",
                                                    sub?.id,
                                                  );
                                                  if (
                                                    originalValueRef.current[
                                                      key
                                                    ] === undefined
                                                  ) {
                                                    originalValueRef.current[
                                                      key
                                                    ] = sub?.value ?? "";
                                                  }
                                                }}
                                                onChange={(e) =>
                                                  updateCompositeValueUI(
                                                    report?.medical_test_id,
                                                    sub?.id,
                                                    e.target.value,
                                                  )
                                                }
                                                onBlur={(e) =>
                                                  handleEditMedicalReportValueComp(
                                                    sub,
                                                    e.target.value,
                                                  )
                                                }
                                                sx={textfieldText}
                                              />
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  openDeleteModal({
                                                    type: "subtest",
                                                    ids: [sub?.id],
                                                    label: sub?.name,
                                                  })
                                                }
                                              >
                                                <DeleteForeverOutlinedIcon
                                                  sx={{
                                                    fontSize: 18,
                                                    color: "red",
                                                  }}
                                                />
                                              </IconButton>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ),
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    }

                    // ✅ NORMAL REPORT
                    return (
                      <TableRow
                        key={report?.id ?? index}
                        sx={{
                          ...tableRowLab,
                          borderBottom:
                            index !== reportsUI.length - 1
                              ? "1px solid #E1E1E1"
                              : "none",
                          backgroundColor: "#F9FBFF",
                        }}
                      >
                        <TableCell sx={{ textAlign: "center" }}>
                          <Typography
                            sx={{ fontSize: "15px", fontWeight: 600 }}
                          >
                            {index + 1}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: "center" }}>
                          <Typography
                            sx={{ fontSize: "15px", fontWeight: 600 }}
                          >
                            {report?.test_name}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ textAlign: "center" }}>
                          {report?.generated_at}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {report?.critical ?? "-"}
                        </TableCell>
                        <TableCell sx={{ textAlign: "center" }}>
                          {report?.reference_range?.male_min_max ?? "-"}
                        </TableCell>

                        <TableCell sx={{ textAlign: "center" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                              py: 0.5,
                            }}
                          >
                            <TextField
                              value={report?.value ?? ""}
                              onFocus={() => {
                                const key = makeKey("normal", report?.id);
                                if (
                                  originalValueRef.current[key] === undefined
                                ) {
                                  originalValueRef.current[key] =
                                    report?.value ?? "";
                                }
                              }}
                              onChange={(e) =>
                                updateReportValueUI(report?.id, e.target.value)
                              }
                              onBlur={(e) =>
                                handleEditMedicalReportValue(
                                  report,
                                  e.target.value,
                                )
                              }
                              sx={textfieldText}
                            />
                            <IconButton
                              size="small"
                              onClick={() =>
                                openDeleteModal({
                                  type: "normal",
                                  ids: [report?.id],
                                  label: report?.test_name,
                                })
                              }
                            >
                              <DeleteForeverOutlinedIcon
                                sx={{ fontSize: 18, color: "red" }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}
      {/* //modals for delete and filter */}
      <Modal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        sx={{
          position: "absolute",
          top: "25%",
          width: "450px",
          margin: "auto",
          borderRadius: "20px",
        }}
      >
        <Box
          sx={{
            mt: 2,
            bgcolor: "background.paper",
            borderRadius: 3.6,
            boxShadow: 24,
            p: 4.5,
            "&:focus": { outline: "none" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography textAlign="center" fontWeight={700}>
              Are you sure?
            </Typography>
            <Typography textAlign="center">
              You want to delete <b>{deleteTarget?.label || "this report"}</b> ?
            </Typography>

            {deleteTarget?.type === "composite" && (
              <Typography
                textAlign="center"
                sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}
              >
                * This will delete the whole composite parent report.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "25px",
              width: "max-content",
              margin: "auto",
              marginTop: "40px",
            }}
          >
            <Button
              onClick={handleDelete}
              sx={{
                backgroundColor: "#FF392B",
                color: "white",
                width: "150px",
                borderRadius: "5px",
                fontSize: "12px",
                textTransform: "capitalize",
                "&:hover": { backgroundColor: "red" },
              }}
            >
              Delete
            </Button>

            <Button
              onClick={() => setDeleteModal(false)}
              sx={{
                backgroundColor: "white",
                color: "black",
                border: "2px solid #FF392B",
                width: "150px",
                borderRadius: "5px",
                fontSize: "12px",
                textTransform: "capitalize",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={deleteModalFile}
        onClose={() => setDeleteModalFile(false)}
        sx={{
          position: "absolute",
          top: "25%",
          width: "450px",
          margin: "auto",
          borderRadius: "20px",
        }}
      >
        <Box
          sx={{
            mt: 2,
            bgcolor: "background.paper",
            borderRadius: 3.6,
            boxShadow: 24,
            p: 4.5,
            "&:focus": { outline: "none" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography textAlign="center" fontWeight={700}>
              Are you sure?
            </Typography>
            <Typography textAlign="center">
              You want to delete <b>{deleteTarget?.label || "this report"}</b> ?
            </Typography>

            {deleteTarget?.type === "composite" && (
              <Typography
                textAlign="center"
                sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}
              >
                * This will delete the whole composite parent report.
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "25px",
              width: "max-content",
              margin: "auto",
              marginTop: "40px",
            }}
          >
            <Button
              onClick={() => deleteAttachmentUI(deleteFileTarget?.id)}
              sx={{
                backgroundColor: "#FF392B",
                color: "white",
                width: "150px",
                borderRadius: "5px",
                fontSize: "12px",
                textTransform: "capitalize",
                "&:hover": { backgroundColor: "red" },
              }}
            >
              Delete
            </Button>

            <Button
              onClick={() => setDeleteModalFile(false)}
              sx={{
                backgroundColor: "white",
                color: "black",
                border: "2px solid #FF392B",
                width: "150px",
                borderRadius: "5px",
                fontSize: "12px",
                textTransform: "capitalize",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={viewerOpen}
        onClose={closeViewer}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
            position: "relative",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              height: 54,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {viewerFile?.filename || "Preview"}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {/* optional: open in new tab */}
              <Button
                size="small"
                // variant="outlined"
                onClick={() =>
                  viewerFile?.url && window.open(viewerFile.url, "_blank")
                }
              >
                <ScreenShareOutlinedIcon sx={{ color: "neutral.teal" }} />{" "}
              </Button>

              <IconButton onClick={closeViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ width: "100%", height: "calc(90vh - 54px)" }}>
            {viewerFile?.content_type === "application/pdf" ? (
              <iframe
                title="pdf-viewer"
                src={viewerFile?.url}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : viewerFile?.content_type?.startsWith("image/") ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#000",
                }}
              >
                <img
                  src={viewerFile?.url}
                  alt={viewerFile?.filename || "image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>
                  Preview not supported for this file type.
                </Typography>
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() =>
                    viewerFile?.url && window.open(viewerFile.url, "_blank")
                  }
                >
                  Download / Open
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ViewExternalReports;
